import { Button, Checkbox, Divider, Form, Input, message, Tooltip } from "antd";
import { ErrorMessages } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import {
  FeatureIdTemplateMapping,
  FeatureRouteSlugMapping,
  FeatureTypeStringMapping,
  FeatureTypes
} from "entity-app/constants";
import { useContext, useMemo, useState } from "react";
import { useParams } from "react-router";
import { validatorForFieldValue } from "utils/utils";

const requiresNumberField = (featureType: FeatureTypes) =>
  featureType === FeatureTypes.DESIGN_PACKAGE ||
  featureType === FeatureTypes.BID_PACKAGE;

const supportsCopyLinks = (featureType: FeatureTypes) =>
  featureType === FeatureTypes.SUBMITTALS ||
  featureType === FeatureTypes.MATERIALS;

const getTitleUniquenessError = (featureType: FeatureTypes) => {
  switch (featureType) {
    case FeatureTypes.SUBMITTALS:
      return ErrorMessages.SubmittalTitleMsg;
    case FeatureTypes.MATERIALS:
      return ErrorMessages.MaterialTitleMsg;
    case FeatureTypes.BID_PACKAGE:
      return ErrorMessages.BidPackageTitleMsg;
    case FeatureTypes.DESIGN_PACKAGE:
      return ErrorMessages.DesignPackageTitleMsg;
    default:
      return ErrorMessages.uniquessViolation;
  }
};

const getNumberUniquenessError = (featureType: FeatureTypes) => {
  switch (featureType) {
    case FeatureTypes.BID_PACKAGE:
      return ErrorMessages.BidPackageNumberMsg;
    case FeatureTypes.DESIGN_PACKAGE:
      return ErrorMessages.DesignPackageNumberMsg;
    // Only called from the number field, which renders only when
    // requiresNumberField() proved the type is BID_PACKAGE or DESIGN_PACKAGE.
    /* v8 ignore next 2 */
    default:
      return ErrorMessages.uniquessViolation;
  }
};

const getRequiredTitleMessage = (featureType: FeatureTypes) => {
  switch (featureType) {
    case FeatureTypes.SUBMITTALS:
      return ErrorMessages.submittalTitle;
    case FeatureTypes.MATERIALS:
      return ErrorMessages.materialTitle;
    case FeatureTypes.BID_PACKAGE:
      return ErrorMessages.bidPackgeTitle;
    case FeatureTypes.DESIGN_PACKAGE:
      return ErrorMessages.designPackgeTitle;
    default:
      return "Enter title.";
  }
};

const getRequiredNumberMessage = (featureType: FeatureTypes) => {
  switch (featureType) {
    case FeatureTypes.BID_PACKAGE:
      return ErrorMessages.bidPackgeNumber;
    case FeatureTypes.DESIGN_PACKAGE:
      return ErrorMessages.designPackgeNumber;
    // Only called from the number field, which renders only when
    // requiresNumberField() proved the type is BID_PACKAGE or DESIGN_PACKAGE.
    /* v8 ignore next 2 */
    default:
      return "Enter number.";
  }
};

const getDuplicateHeaderText = (
  featureType: FeatureTypes,
  featureName: string
) => {
  if (requiresNumberField(featureType)) {
    return `Provide ${featureName} Number and Title for the duplicate.`;
  }
  return `Provide ${featureName} Title for the duplicate.`;
};

export default function FeatureDuplicate(
  props: Readonly<{
    currentFeatureType: FeatureTypes;
    featureInstanceData: any;
    actions?: any;
    linkedSourcefeatureType?: FeatureTypes;
    setVisible?: (open: boolean) => void;
    visible?: boolean;
  }>
) {
  const {
    currentFeatureType,
    featureInstanceData,
    actions,
    linkedSourcefeatureType,
    setVisible,
    visible
  } = props;
  const { projectId } = useParams() as any;
  const { tokenRetrievalState } = useContext(ProjectContext);
  const token = tokenRetrievalState?.token;
  const showNumberField = requiresNumberField(currentFeatureType);
  const showCopyLinks = supportsCopyLinks(currentFeatureType);
  const [form] = Form.useForm();

  const [isIncludeLinks, setIncludeLinks] = useState({
    features: false,
    activities: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const featureName = useMemo(() => {
    return FeatureIdTemplateMapping[currentFeatureType] || "";
  }, [currentFeatureType]);

  const featureTypeString = useMemo(() => {
    return FeatureTypeStringMapping[currentFeatureType] || "";
  }, [currentFeatureType]);

  const featureLinks = useMemo(() => {
    if (!linkedSourcefeatureType) return [];
    switch (linkedSourcefeatureType) {
      case FeatureTypes.SUBMITTALS:
        return featureInstanceData?.links_as_target?.filter(
          (link: any) =>
            link?.source_feature_instance?.feature_type_id ===
              FeatureTypes.SUBMITTALS ||
            link?.source_feature_instance?.feature_type_id ===
              FeatureTypes.PLANNING_ONLY_SUBMITTAL
        );
      case FeatureTypes.MATERIALS:
        return featureInstanceData?.links_as_source?.filter(
          (link: any) =>
            link?.target_feature_instance?.feature_type_id ===
            FeatureTypes.MATERIALS
        );

      default:
        return [];
    }
  }, [
    featureInstanceData?.links_as_source,
    featureInstanceData?.links_as_target,
    linkedSourcefeatureType
  ]);

  const featureActivityLinks = useMemo(() => {
    return featureInstanceData?.links_as_source?.filter(
      (link: any) =>
        link?.target_feature_instance?.feature_type_id ===
        FeatureTypes.ACTIVITIES
    );
  }, [featureInstanceData?.links_as_source]);

  const featureLinkedTitles = () => {
    switch (currentFeatureType) {
      case FeatureTypes.SUBMITTALS:
        return {
          sourceTitle: "Linked Materials",
          targetTitle: "Linked Activities"
        };
      case FeatureTypes.MATERIALS:
        return {
          sourceTitle: "Linked Submittals",
          targetTitle: "Linked Activities"
        };
      // Only called from the copy-links block, which renders only when
      // supportsCopyLinks() proved the type is SUBMITTALS or MATERIALS.
      /* v8 ignore start */
      default:
        return {
          sourceTitle: "",
          targetTitle: ""
        };
      /* v8 ignore stop */
    }
  };

  const messageText = (newFeatureId: string) => {
    const featureSlug =
      FeatureRouteSlugMapping[currentFeatureType] ||
      `${featureName}s`.toLowerCase().replace(/\s+/g, "-");
    return (
      <div>
        {`Duplicate ${featureName} created. Click`}
        <span className="mx-1">
          <a
            href={`/project/${projectId}/${featureSlug}/${newFeatureId}`}
            className="underline"
            data-testid="index-link"
          >
            here
          </a>
        </span>
        to view details.
      </div>
    );
  };

  const linksParams = () => {
    if (!showCopyLinks) {
      return {
        linked_source_feature_types: [],
        linked_target_feature_types: []
      };
    }

    const type: number[] = [];
    if (isIncludeLinks.activities && isIncludeLinks.features) {
      type.push(FeatureTypes.MATERIALS, FeatureTypes.ACTIVITIES);
    } else if (isIncludeLinks.activities && !isIncludeLinks.features) {
      type.push(FeatureTypes.ACTIVITIES);
    } else if (isIncludeLinks.features && !isIncludeLinks.activities) {
      type.push(FeatureTypes.MATERIALS);
    }

    switch (currentFeatureType) {
      case FeatureTypes.SUBMITTALS:
        return {
          linked_source_feature_types: [],
          linked_target_feature_types: type
        };
      case FeatureTypes.MATERIALS:
        return {
          linked_source_feature_types: isIncludeLinks.features
            ? [FeatureTypes.SUBMITTALS, FeatureTypes.PLANNING_ONLY_SUBMITTAL]
            : [],
          linked_target_feature_types: isIncludeLinks.activities
            ? [FeatureTypes.ACTIVITIES]
            : []
        };
      // Unreachable: the !showCopyLinks guard above already returned for every
      // type other than SUBMITTALS and MATERIALS, both handled here.
      /* v8 ignore start */
      default:
        return {
          linked_source_feature_types: [],
          linked_target_feature_types: []
        };
      /* v8 ignore stop */
    }
  };

  const onFinish = async (values: { title: string; number?: string }) => {
    if (setVisible) {
      setVisible(!visible);
    }

    setIsSubmitting(true);
    try {
      const payload: {
        title: string;
        feature_instance_id: string;
        number?: string;
        linked_source_feature_types: number[];
        linked_target_feature_types: number[];
      } = {
        ...linksParams(),
        title: values.title.trim(),
        feature_instance_id: featureInstanceData?.id
      };

      if (showNumberField) {
        payload.number = values.number?.trim();
      }

      const response = await actions?.onDuplicateFeature(payload);
      if (response?.success) {
        message.success(
          messageText(response.data?.duplicate_feature_instance?.id),
          10
        );
      }
      if (response?.errors) {
        message.error(response.errors[0].message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!featureInstanceData) return null;

  return (
    <div className="flex flex-col text-xs w-72 space-y-2">
      <div className="flex text-xs">
        {getDuplicateHeaderText(currentFeatureType, featureName)}
      </div>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{
          title: `${featureInstanceData?.title} (copy)`,
          number: ""
        }}
        onFinish={onFinish}
        className="!mb-0"
      >
        {showNumberField && (
          <Form.Item
            name="number"
            label={<span className="font-semibold text-xs">Number</span>}
            className="!mb-2"
            rules={[
              {
                required: true,
                whitespace: true,
                message: getRequiredNumberMessage(currentFeatureType)
              },
              {
                validator: (_, value) => {
                  if (!value?.trim() || !featureTypeString) {
                    return Promise.resolve();
                  }
                  return validatorForFieldValue(
                    "number",
                    value.trim(),
                    featureTypeString,
                    token,
                    getNumberUniquenessError(currentFeatureType)
                  );
                }
              }
            ]}
          >
            <Input data-testid="index-input-number" />
          </Form.Item>
        )}
        <Form.Item
          name="title"
          label={<span className="font-semibold text-xs">Title</span>}
          className="!mb-2"
          rules={[
            {
              required: true,
              whitespace: true,
              message: getRequiredTitleMessage(currentFeatureType)
            },
            {
              validator: (_, value) => {
                if (!value?.trim() || !featureTypeString) {
                  return Promise.resolve();
                }
                return validatorForFieldValue(
                  "title",
                  value.trim(),
                  featureTypeString,
                  token,
                  getTitleUniquenessError(currentFeatureType)
                );
              }
            }
          ]}
        >
          <Input data-testid="index-input-name" />
        </Form.Item>

        {showCopyLinks && (
          <div className="flex flex-col text-xs pt-2 pb-1 space-y-2">
            <div>Do you want to copy the links from original ?</div>
            <div className="flex justify-between">
              <Tooltip
                title={
                  featureLinks?.length < 1 ? ErrorMessages.NoLinksPresent : null
                }
              >
                <Checkbox
                  className="text-xs"
                  checked={featureLinks?.length > 0 && isIncludeLinks.features}
                  disabled={!featureLinks?.length}
                  onChange={(event) => {
                    setIncludeLinks((pre) => ({
                      ...pre,
                      features: event.target.checked
                    }));
                  }}
                >
                  {featureLinkedTitles().sourceTitle}
                </Checkbox>
              </Tooltip>
              <Tooltip
                title={
                  featureActivityLinks?.length < 1
                    ? ErrorMessages.NoLinksPresent
                    : null
                }
              >
                <Checkbox
                  className="text-xs"
                  checked={
                    featureActivityLinks?.length > 0 &&
                    isIncludeLinks.activities
                  }
                  disabled={!featureActivityLinks?.length}
                  onChange={(event) => {
                    setIncludeLinks((pre) => ({
                      ...pre,
                      activities: event.target.checked
                    }));
                  }}
                >
                  {featureLinkedTitles().targetTitle}
                </Checkbox>
              </Tooltip>
            </div>
          </div>
        )}
        <Divider className="py-1" />

        <div className="flex justify-end space-x-2 text-xs">
          <Button
            onClick={() => {
              if (setVisible) {
                setVisible(false);
              }
            }}
            data-testid="index-button-cancel"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            data-testid="index-button-create"
          >
            Create
          </Button>
        </div>
      </Form>
    </div>
  );
}
